/**
 * El copy del paciente (ADR-016).
 *
 * Es el módulo que da sentido a todo el ejercicio: **el texto que lee el
 * paciente es material clínico-comunicacional que Fundación tiene que poder
 * revisar sin leer código**. Ya lo hizo con las frases de la salida segura.
 * Todo lo que dice una pantalla del paciente se escribe acá y se cambia acá.
 *
 * **Dos cosas que NO viven en este módulo, y no es un olvido:**
 *
 *   - **La redacción de las escalas.** Los enunciados, las anclas y las
 *     etiquetas de los instrumentos vienen de `@pasos/schemas` y están
 *     congelados en los `Questionnaire` FHIR. Cambiarlos ahí es cambiar el
 *     instrumento; moverlos acá los volvería editables por error.
 *   - **El texto del consentimiento informado.** Llega del servidor y está
 *     versionado en `consent_record` con su hash: la prueba de qué leyó la
 *     persona es ese texto y no una constante del frontend. Lo que sí está acá
 *     es el andamiaje de la pantalla que lo muestra.
 *
 * Regla del ADR: el copy es dato. Cada entrada es una **frase entera**; donde
 * hay que intercalar un valor, la función devuelve la frase completa, y donde
 * hay variantes, cada variante es su propia entrada legible de punta a punta.
 */
/** Las tres variantes de la cuenta del próximo contacto, como frases enteras. */
const PACIENTE_INICIO_CUENTA = Object.freeze({
  hoy: 'hoy',
  unDia: 'en 1 día',
  variosDias: (dias: number) => `en ${dias} días`,
});

export const PACIENTE = Object.freeze({
  // ── P-01 · el enlace de acceso ────────────────────────────────────────────
  acceso: {
    validando: 'Estamos confirmando tu identidad…',
    // PROPUESTO · pendiente de validación de Fundación (referencia visual E3.8)
    lema: 'Tu recuperación, paso a paso.',
    sinTokenTitulo: 'Para entrar necesitás tu enlace',
    // PROPUESTO · pendiente de validación de Fundación (devolución 04/10)
    sinTokenDetalle:
      'Tu equipo médico te da un enlace o un código QR para abrir PASOS+ por primera vez. Si no lo tenés o ya no funciona, pedíselo.',
    vencidoTitulo: 'Este enlace ya no sirve',
    vencidoDetalle:
      'Los enlaces vencen por seguridad. Pedile uno nuevo a tu equipo médico y volvé a entrar.',
    reintentar: 'Probar de nuevo',
    // ── INGRESO-2 · el código de activación, en la app instalada ──
    // PROPUESTO · pendiente de validación de Fundación (INGRESO-2)
    codigoRotulo: '¿Tenés un código de 6 números?',
    // PROPUESTO · pendiente de validación de Fundación (INGRESO-2)
    codigoDetalle:
      'Es el que te mostramos cuando agregaste PASOS+ a tu pantalla de inicio. Si no lo tenés, pedile a tu equipo médico un enlace nuevo.',
    // PROPUESTO · pendiente de validación de Fundación (INGRESO-2)
    codigoEtiqueta: 'Código de 6 números',
    codigoEntrar: 'Entrar',
    // PROPUESTO · pendiente de validación de Fundación (INGRESO-2) · el mismo para todo rechazo
    codigoNoSirve:
      'Ese código no es correcto o ya venció. Pedile a tu equipo médico un enlace nuevo.',
    // PROPUESTO · pendiente de validación de Fundación (INGRESO-2)
    codigoFrenado: 'Probaste muchas veces. Esperá unos minutos y volvé a intentar.',
    // PROPUESTO · pendiente de validación de Fundación (INGRESO-2) · sin respuesta del servidor
    codigoSinRespuesta: 'No pudimos comprobar el código. Revisá la conexión y volvé a intentar.',
  },

  // ── INGRESO-2 · el código de activación, en Safari ────────────────────────
  // Un solo bloque para el paso de instalación (P-02d) y para Perfil (P-13) del
  // paciente y del cuidador.
  activacion: {
    // PROPUESTO · pendiente de validación de Fundación (INGRESO-2)
    presentacion: 'Para abrir PASOS+ desde el ícono vas a necesitar un código.',
    // PROPUESTO · pendiente de validación de Fundación (INGRESO-2)
    mostrar: 'Mostrar mi código',
    // PROPUESTO · pendiente de validación de Fundación (INGRESO-2)
    vale: 'Vale por 10 minutos y una sola vez.',
    // PROPUESTO · pendiente de validación de Fundación (INGRESO-2) · los tres pasos, uno por renglón
    pasos: [
      'Agregá PASOS+ a tu pantalla de inicio.',
      'Abrilo desde el ícono.',
      'Escribí este código.',
    ],
    pasosParaLector: 'Cómo usarlo',
    // El lector de pantalla lo lee dígito por dígito: "4 8 2 9 1 3".
    codigoParaLector: (codigo: string) => `Tu código es ${codigo.split('').join(' ')}`,
    // PROPUESTO · pendiente de validación de Fundación (INGRESO-2)
    vencido: 'Este código venció.',
    // PROPUESTO · pendiente de validación de Fundación (INGRESO-2)
    otro: 'Mostrar uno nuevo',
    // PROPUESTO · pendiente de validación de Fundación (INGRESO-2)
    errorAlPedir: 'No pudimos pedir el código. Probá de nuevo en un momento.',
  },

  // ── P-02 · onboarding ─────────────────────────────────────────────────────
  onboarding: {
    buscandoDondeQuedaste: 'Buscando dónde quedaste…',
    paso: (numero: number, total: number) => `Paso ${numero} de ${total}`,
    pasoActualParaLector: (numero: number) => `Paso ${numero}, actual`,
    pasoParaLector: (numero: number) => `Paso ${numero}`,
    progresoParaLector: 'Progreso del onboarding',
    continuar: 'Continuar',

    identidad: {
      titulo: '¡Hola! Soy PIA',
      presentacion:
        'Te voy a acompañar durante tu recuperación. Para empezar, necesito confirmar algunos datos.',
      nombreEtiqueta: 'Tu nombre',
      nombreAyuda: 'Así te vamos a llamar en la app.',
      cirugiaEtiqueta: 'Fecha de tu cirugía',
      cirugiaAyuda: 'Si no es correcta, avisale a tu equipo médico.',
      cirugiaFalta: 'Necesitamos la fecha de tu cirugía para continuar.',
      errorAlGuardar: 'No pudimos guardar los datos. Probá de nuevo.',
    },

    // El texto legal no está acá: llega del servidor, versionado en
    // `consent_record`. Esto es la pantalla que lo muestra.
    consentimiento: {
      titulo: 'Consentimiento',
      instruccion: 'Antes de empezar, leé y aceptá el consentimiento informado.',
      cargando: 'Cargando el consentimiento…',
      errorTitulo: 'No pudimos cargar el texto',
      errorDetalle: 'No pudimos traer el texto del consentimiento. Sin leerlo no podés continuar.',
      regionParaLector: 'Texto del consentimiento informado',
      faltaLeer: 'Deslizá hasta el final para poder aceptar.',
      leido: 'Llegaste al final. Ya podés aceptar.',
      version: (version: string) => `Versión ${version}`,
      aceptar: 'Acepto y continúo',
      errorAlAceptar: 'No pudimos registrar tu aceptación. Probá de nuevo.',
    },

    cuidador: {
      titulo: 'Tu contacto de apoyo',
      presentacion:
        'Si querés, podés sumar a alguien de confianza que te ayude con la app. Es opcional: podés saltearlo.',
      vinculoEtiqueta: 'Vínculo',
      vinculoAyuda: 'Por ejemplo: hija, esposo, vecina.',
      vinculoFalta: 'Contanos qué vínculo tienen.',
      celularEtiqueta: 'Celular',
      celularAyuda: 'Celular con característica. Le vamos a mandar la invitación por WhatsApp.',
      celularFalta: 'Necesitamos un celular para poder invitarlo.',
      veTitulo: 'Qué va a poder ver',
      veEstado: 'Tu estado general en el programa.',
      /**
       * H-18. Decía "Avisos del equipo, si hiciera falta", y el panel del
       * cuidador no tiene avisos del equipo ni hay forma de escribirlos: lo
       * único que puede llegarle es la consulta automática del protocolo de
       * silencio. La frase ahora describe eso y nada más.
       *
       * En segunda persona porque **la lee el paciente**, en su onboarding,
       * sobre lo que va a ver su contacto de apoyo.
       */
      veAvisos: 'Una consulta del programa, si dejás de responder.',
      noVeTitulo: 'Qué NO va a ver',
      noVeConversaciones: 'Tus conversaciones con PIA.',
      noVeClinico: 'Tus datos clínicos detallados ni tus evaluaciones.',
      saltar: 'Saltar este paso',
      errorAlGuardar: 'No pudimos guardar el contacto. Probá de nuevo.',
    },

    permisos: {
      titulo: 'Casi listo',
      presentacion: 'Para aprovechar bien la app:',
      pushTitulo: 'Activar notificaciones',
      /**
       * H-4. Decía "un check-in o un mensaje del equipo". **Un mensaje del
       * equipo no existe como función**: la comunicación del equipo al paciente
       * es una decisión todavía abierta con Fundación. El aviso del check-in sí
       * está planificado, y es deuda de E5 con su escenario.
       */
      pushDetalle: 'Te avisamos cuando te toca un check-in.',
      pushActivar: 'Activar',
      pushReintentar: 'Volver a intentar',
      pushActivadas: 'Notificaciones activadas.',
      /**
       * H-4. Decía "podés activarlas más adelante desde tu perfil". El perfil
       * **muestra** el estado de los avisos y no tiene botón para activarlos:
       * era falso hoy. Se sacó la promesa en vez de inventar el botón, que es
       * de E5 junto con el envío.
       */
      pushRechazadas: 'No pasa nada: la app funciona igual.',
      instalarTitulo: 'Agregar a tu pantalla de inicio',
      instalarDetalle:
        'Para abrir PASOS+ con un toque, sin buscarlo. En el menú del navegador, elegí “Agregar a pantalla de inicio”.',
      terminar: 'Empezar mi programa',
      errorAlTerminar: 'No pudimos terminar. Probá de nuevo.',
    },

    listo: {
      titulo: 'Todo listo',
      detalle: 'Tu programa arrancó. Te vamos a acompañar durante los próximos 365 días.',
      irAlCheckin: 'Responder mi check-in',
    },
  },

  // ── Tab bar ───────────────────────────────────────────────────────────────
  navegacion: {
    etiqueta: 'Secciones',
    inicio: 'Inicio',
    pia: 'PIA',
    pasos: 'Pasos',
    perfil: 'Perfil',
    seccionActual: '(sección actual)',
    // PROPUESTO · pendiente de validación de Fundación (referencia visual E3.8)
    volver: 'Volver',
  },

  // ── P-03 · el home ────────────────────────────────────────────────────────
  inicio: {
    // PROPUESTO · pendiente de validación de Fundación (respuesta 2.8 del 28/10):
    // la app no nombra al paciente en el saludo. Su alias es "Caso" y un número.
    saludo: '¡Hola!',
    diaDelPrograma: (dia: number, total: number) => `Día ${dia} de ${total}`,
    reflejoTitulo: 'La última vez nos contaste…',
    // Una frase entera por respuesta (ADR-016): nada de intercalar la etiqueta
    // de la opción en minúscula. La frase sale del código de la respuesta y del
    // microcheck del que vino, porque la pregunta no es la misma en los dos. Un
    // código que no está acá no se muestra (E3.11).
    reflejoSemanal: {
      mejor: 'que te movías mejor que la vez anterior.',
      igual: 'que te movías igual que la vez anterior.',
      peor: 'que te movías peor que la vez anterior.',
    },
    // PROPUESTO · pendiente de validación de Fundación (E3.11) · las respuestas del microcheck mensual
    reflejoMensual: {
      todas: 'que podías hacer todas las actividades del día a día.',
      'la-mayoria': 'que podías hacer la mayoría de las actividades del día a día.',
      pocas: 'que podías hacer pocas de las actividades del día a día.',
      'casi-ninguna': 'que casi no podías hacer las actividades del día a día.',
    },
    // PROPUESTO · pendiente de validación de Fundación (E3.11) · al paciente no se le muestra el código del hito
    proximoContacto: 'Tu próximo contacto',
    // PROPUESTO · pendiente de validación de Fundación (E3.11)
    proximoCheckin: 'Check-in',
    // PROPUESTO · pendiente de validación de Fundación (E3.11)
    proximaEvaluacion: 'Evaluación',
    // La fecha y la cuenta van en dos renglones pensados y no en uno que se
    // corta donde cae (E3.12): arriba la fecha, abajo la cuenta.
    fechaLegible: (diaDeLaSemana: string, dia: string, mes: string) =>
      `${diaDeLaSemana} ${dia} de ${mes}`,
    /**
     * La cuenta, entera, para el número que sea.
     *
     * La pantalla no arma la frase ni elige entre tres entradas (ADR-016): pide
     * la cuenta y recibe la frase escrita. Las tres variantes siguen siendo
     * entradas propias, acá arriba.
     */
    cuenta: (dias: number): string =>
      dias <= 0
        ? PACIENTE_INICIO_CUENTA.hoy
        : dias === 1
          ? PACIENTE_INICIO_CUENTA.unDia
          : PACIENTE_INICIO_CUENTA.variosDias(dias),
    sinContactos: 'Ya pasaste todos',
    paraHoy: 'Para hoy',
    unaTarea: 'tarea',
    variasTareas: 'tareas',
    pendientesTitulo: 'Tus pendientes',
    sinPendientesTitulo: 'Nada pendiente por hoy',
    sinPendientesDetalle:
      'Cuando toque tu próximo contacto te vamos a avisar. No tenés que hacer nada ahora.',
    pendienteEscala: 'Evaluación de seguimiento',
    // PROPUESTO · pendiente de validación de Fundación (devolución 04/10)
    // Decisión de Mariano: el tiempo solo, al lado de un reloj, sin contar
    // escalas. Son unas 25 preguntas; se revisa con el tiempo real cuando haya uso.
    pendienteEscalaDetalle: '5 minutos',
    pendienteCheckin: 'Check-in del día',
    // PROPUESTO · pendiente de validación de Fundación · H-1: lo que se respondió
    // sin conexión está guardado y no se pide de nuevo.
    guardadoTitulo: 'Tus respuestas están guardadas',
    // PROPUESTO · pendiente de validación de Fundación · H-1
    guardadoDetalle: 'Se van a enviar solas cuando haya conexión. No tenés que responder de nuevo.',
    pendienteCheckinDetalle: '2 minutos',
    irAReportar: 'Reportar algo que me pasó',
    // PROPUESTO · pendiente de validación de Fundación (referencia visual E3.8)
    siNecesitasAlgo: 'Si necesitás algo',
    // PROPUESTO · pendiente de validación de Fundación (referencia visual E3.8)
    hablarConPia: 'Hablar con PIA',
    // PROPUESTO · pendiente de validación de Fundación (referencia visual E3.8)
    piaDetalle: 'Tu asistente del programa',
  },

  // ── P-05 y P-06 · responder un formulario ─────────────────────────────────
  // El enunciado de cada pregunta viene del instrumento, no de acá.
  formulario: {
    progreso: (numero: number, total: number) => `Pregunta ${numero} de ${total}`,
    sinPreguntas: 'Listo, no queda ninguna pregunta. Podés enviar tus respuestas.',
    enviar: 'Enviar mis respuestas',
    masTarde: 'Preguntame más tarde',
    graciasTitulo: 'Gracias',
    enviado: (titulo: string) => `Registramos tus respuestas de "${titulo}".`,
    enCola: (titulo: string) =>
      `Guardamos tus respuestas de "${titulo}". Se van a enviar solas cuando vuelva la conexión.`,
    // PROPUESTO · pendiente de validación de Fundación (E3.23) · el rechazo
    // definitivo del servidor. No se agradece ni se promete un envío: lo que el
    // sistema no registró, no se dice registrado.
    noSePudoRegistrar: 'No pudimos registrar tus respuestas',
    noSePudoRegistrarDetalle:
      'Guardamos lo que contestaste en este teléfono, pero no quedó registrado. Podés volver a responder desde el inicio.',
    si: 'Sí',
    no: 'No',
    bajarDiez: (paso: number) => `Bajar ${paso} puntos`,
    subirDiez: (paso: number) => `Subir ${paso} puntos`,
    // El número del extremo y su ancla, que viene del instrumento: "0 · Sin dolor" (E3.11).
    ancla: (numero: number, ancla: string) => `${numero} · ${ancla}`,
    bajarUno: 'Bajar 1 punto',
    subirUno: 'Subir 1 punto',
    // PROPUESTO · pendiente de validación de Fundación (referencia visual E3.8)
    volverAlInicio: 'Volver al inicio',
    // PROPUESTO · pendiente de validación de Fundación (referencia visual E3.8) · P-74, aprobado por Mariano en E3.9
    siguiente: 'Siguiente',
    // PROPUESTO · pendiente de validación de Fundación (referencia visual E3.8) · el eco de la respuesta (P-05)
    tuRespuesta: 'Tu respuesta',

    // ── Los cuatro tipos de E3.25: número, fecha, catálogo y grupo ──
    // PROPUESTO · pendiente de validación de Fundación (E3.25) · el rango de una cantidad
    rango: (minimo: string, maximo: string, unidad: string) =>
      `Un número entre ${minimo} y ${maximo} ${unidad}.`,
    // PROPUESTO · pendiente de validación de Fundación (E3.25)
    numeroInvalido: (minimo: string, maximo: string, unidad: string) =>
      `Escribí un número entre ${minimo} y ${maximo} ${unidad}.`,
    // PROPUESTO · pendiente de validación de Fundación (E3.25)
    decimalesDeMas: (decimales: number) =>
      decimales === 0
        ? 'Escribí un número sin coma.'
        : `Hasta ${decimales} números después de la coma.`,
    // PROPUESTO · pendiente de validación de Fundación (E3.25)
    fechaFutura: 'La fecha no puede ser posterior a hoy.',
    // PROPUESTO · pendiente de validación de Fundación (E3.25)
    fechaAnteriorAlEgreso: 'La fecha no puede ser anterior a tu alta.',
    // PROPUESTO · pendiente de validación de Fundación (E3.25) · el buscador del catálogo
    buscar: 'Buscar en la lista',
    // PROPUESTO · pendiente de validación de Fundación (E3.25)
    buscarAyuda: 'Escribí parte del nombre para achicar la lista.',
    // PROPUESTO · pendiente de validación de Fundación (E3.25)
    sinResultados: 'No hay nada con ese nombre en la lista.',
    // PROPUESTO · pendiente de validación de Fundación (E3.25)
    listaSinDescargar:
      'Todavía no pudimos bajar la lista. Si no está lo que buscás, elegí la última opción.',
    // PROPUESTO · pendiente de validación de Fundación (E3.25) · las filas de un grupo
    fila: (etiqueta: string, numero: number) => `${etiqueta} ${numero}`,
    // PROPUESTO · pendiente de validación de Fundación (E3.25)
    agregarOtro: 'Agregar otro',
    // PROPUESTO · pendiente de validación de Fundación (E3.25)
    quitar: (etiqueta: string, numero: number) => `Quitar ${etiqueta.toLowerCase()} ${numero}`,
    // PROPUESTO · pendiente de validación de Fundación (E3.25) · el eco de un grupo, sin género
    enLaLista: (cantidad: number) => (cantidad === 1 ? '1 en la lista' : `${cantidad} en la lista`),
    // PROPUESTO · pendiente de validación de Fundación (E3.25)
    completaLaFila: 'Completá las preguntas de cada uno para seguir.',
  },

  // ── D+360 · lo que contó en el año (E3.25, D-01) ─────────────────────────
  // PROPUESTO · pendiente de validación de Fundación (E3.25) · el total del año
  // no se vuelve a preguntar: se muestra, y la pregunta es si falta alguna.
  loQueContaste: {
    reinternacionesTitulo: 'Las reinternaciones que nos contaste este año',
    reintervencionesTitulo: 'Las cirugías que nos contaste este año',
    ningunaReinternacion: 'No nos contaste ninguna reinternación este año.',
    ningunaReintervencion: 'No nos contaste ninguna cirugía este año.',
    enEstaInstitucion: 'En esta misma institución',
    enOtraInstitucion: 'En otra institución',
    cargando: 'Buscando lo que nos contaste…',
    sinConexion:
      'No pudimos traer lo que nos contaste. Si tuviste alguna que no recordás haber contado, respondé que sí.',
  },

  // ── P-07 · reportar algo ──────────────────────────────────────────────────
  reportar: {
    titulo: 'Contanos qué pasó',
    presentacion: 'Queda registrado en tu seguimiento para que tu equipo lo vea.',
    queTitulo: '¿Qué te pasó?',
    caida: 'Me caí',
    dolorAgudo: 'Dolor fuerte',
    movilidad: 'Me cuesta moverme',
    medicacion: 'Algo con la medicación',
    herida: 'Algo en la herida',
    otro: 'Otra cosa',
    cuandoTitulo: '¿Cuándo fue?',
    ahora: 'Recién',
    hoy: 'Hoy',
    ayer: 'Ayer',
    estaSemana: 'Esta semana',
    severidadTitulo: '¿Qué tan fuerte te pareció?',
    severidadOpcional: '(opcional)',
    leve: 'Leve',
    moderada: 'Moderado',
    alta: 'Fuerte',
    enviar: 'Enviar a mi equipo',
    errorTitulo: 'No se pudo enviar',
    errorDetalle: 'No pudimos registrar tu reporte. Probá de nuevo en un momento.',
    confirmarTitulo: '¿Enviar este reporte?',
    confirmarDetalle:
      'Va a quedar registrado en tu seguimiento y tu equipo lo va a ver. Si necesitás atención ahora, llamá al 107.',
    confirmar: 'Enviar',
    exitoTitulo: 'Quedó registrado',
    // La frase de siempre, en dos bloques: el aviso del 107 gana el suyo (P-07a).
    // Mismas palabras; ninguna cambió.
    exitoDetalle: 'Tu equipo lo va a ver en tu seguimiento.',
    exitoAyuda: 'Si necesitás atención ahora, comunicate con tu centro de salud o llamá al 107.',
    volverAlInicio: 'Volver al inicio',
  },

  // ── PIA · el chat, todavía no disponible ──────────────────────────────────
  pia: {
    titulo: 'PIA',
    presentacion: 'Tu asistente para las dudas del día a día.',
    // PROPUESTO · pendiente de validación de Fundación (PIA-2). Textos de la
    // pantalla: ninguno aconseja nada sobre la salud. Lo que PIA responde es el
    // texto de Fundación, o uno de los dos textos fijos de @pasos/schemas
    // (`TEXTOS_FIJOS_DE_PIA`): la salida segura es texto de Fundación (05/08,
    // copy v2, hoja 5, F20); la derivación sigue PROPUESTA, en los pendientes.
    vacioTitulo: '¿En qué te puedo ayudar?',
    vacioDetalle:
      'Preguntame sobre tu recuperación o sobre el programa. Te respondo con la información que aprobó Fundación Trauma.',
    escribi: 'Escribí tu pregunta',
    enviar: 'Enviar',
    enviando: 'Enviando',
    largo: (usados: number, maximo: number) => `${usados} de ${maximo} caracteres`,
    demasiadoLargo: (maximo: number) => `La pregunta puede tener hasta ${maximo} caracteres.`,
    demasiadosMensajes: 'Esperá un minuto antes de mandar otra pregunta.',
    noSeEnvio: 'No se pudo enviar. Probá de nuevo.',
    aprobadaPor: (codigo: string) => `Información aprobada por Fundación Trauma · ${codigo}`,
    tuMensaje: 'Vos',
    dePia: 'PIA',
    enRevision: 'Contenido en revisión por Fundación Trauma',
    llama107: 'Llamá al 107',
    llamarAl107: 'Llamar al 107',
    cerradaPorSalida:
      'Esta conversación quedó cerrada. Cuando vuelvas a entrar a la app, vas a poder empezar otra.',
    cerradaPorVos: 'Cerraste esta conversación.',
    cerradaPorInactividad: 'Esta conversación se cerró porque pasó un rato sin mensajes.',
    cerrar: 'Terminar la conversación',
    otraConversacion: 'Empezar otra conversación',
    conversacion: 'Conversación con PIA',
    sinConexionTitulo: 'Sin conexión',
    // PROPUESTO · pendiente de validación de Fundación · H-7: sin conexión, un
    // check-in que ya está abierto se puede terminar y sale solo; lo que no se
    // puede es EMPEZAR uno, porque el Inicio los pide al servidor y esa
    // respuesta no se guarda a propósito (los datos clínicos no se cachean).
    // La frase no promete más que eso.
    sinConexionDetalle:
      'PIA necesita conexión. Si ya estabas respondiendo un check-in, podés terminarlo: se envía solo cuando vuelva.',
  },

  // ── P-08 · mi progreso ────────────────────────────────────────────────────
  progreso: {
    titulo: 'Mi progreso',
    diaDelPrograma: (dia: number, total: number) => `${dia} de ${total}`,
    estasEnElDia: 'Estás en el día',
    barraParaLector: (porcentaje: number) => `${porcentaje} por ciento del programa recorrido`,
    recorridoTitulo: 'Tu recorrido',
    sinRecorridoTitulo: 'Tu recorrido arranca pronto',
    sinRecorridoDetalle: 'Tu primer contacto está por llegar. Te avisamos cuando toque.',
    cumplido: 'Completado',
    enCurso: 'Estás acá',
    pendiente: 'Próximo',
    sinResponder: 'No respondido',
    excluido: 'No corresponde',
    microcheck: 'Check-in',
    escala: 'Evaluación',
    teleconsulta: 'Teleconsulta',
    diaDelHito: (dia: number, estado: string) => `Día ${dia} · ${estado}`,
    // PROPUESTO · pendiente de validación de Fundación (referencia visual E3.8) · los rótulos de las etapas de la referencia
    etapasParaLector: 'Etapas del programa',
    etapas: ['Mes 1 a 3', '4 a 6', '7 a 11', '12'],
    porcentaje: (porcentaje: number) => `${porcentaje}%`,
  },

  // PROPUESTO · pendiente de validación de Fundación (CIERRES-1): "Tu programa
  // terminó". Los mensajes que la sección I le asigna a PIA quedan acá, en
  // pantalla, hasta que E4 los conecte. **Ningún consejo de salud:** el texto de
  // "Para seguir cuidándote" lo manda Fundación (TEXTO_DE_CIERRE_DE_LA_RED, P-138).
  fin: {
    titulo: 'Tu programa terminó',
    positivoTexto: 'Completaste el seguimiento de PASOS+. Gracias por cada respuesta.',
    paraSeguirTitulo: 'Para seguir cuidándote',
    contacto: 'Si necesitás algo, comunicate con tu institución.',
    abandonoTexto: 'Respetamos tu decisión. Si en algún momento querés retomar, podés volver.',
    derivacionTexto: (institucion: string) => `Tu seguimiento sigue en ${institucion}.`,
    derivacionSinDestino: 'Tu seguimiento sigue en otra institución.',
  },

  // ── P-13 · mi perfil ──────────────────────────────────────────────────────
  perfil: {
    diaEInstitucion: (dia: number, institucion: string) => `Día ${dia} · ${institucion}`,
    programaTitulo: 'Mi programa',
    institucion: 'Institución',
    cirugia: 'Fecha de la cirugía',
    cirugiaSinRegistrar: 'Sin registrar',
    apoyoTitulo: 'Mi contacto de apoyo',
    apoyoNombre: 'Nombre',
    apoyoVinculo: 'Vínculo',
    sinApoyoTitulo: 'Sin contacto de apoyo',
    sinApoyoDetalle:
      'Podés sumar a alguien de confianza para que te acompañe. Pedíselo a tu equipo médico.',
    preferenciasTitulo: 'Preferencias',
    push: 'Avisos en el celular',
    pushSinDecidir: 'Sin decidir',
    pushActivados: 'Activados',
    pushDesactivados: 'Desactivados',
    // PROPUESTO · pendiente de validación de Fundación (referencia visual E3.8)
    teAcompana: 'Te acompaña',
    // PROPUESTO · pendiente de validación de Fundación (INGRESO-2)
    otroTelefono: 'Abrir PASOS+ en otro teléfono o desde el ícono',
  },
});
